import { ImagesHolder } from "../../../image_loader/images_holder.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { NineSliceDrawer } from "../../../image_loader/nine_slicer/nine_slice_drawer.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { applyNineSliceBorderImage, nineSliceBorderImage } from "../../dom_backend/dom_nine_slice.js";

/** One blue part of the key bar in px, relative to the fill element. */
export interface KeybarPart
{
  x: number;
  w: number;
}

const FILL_RULE = "key_hunt_keybar_fill";

/**
 * The key bar's fill as one bar seen through the three blue parts: every part clips the same
 * nine-slice fill to its own pill, so a full part matches its pill exactly and the growing end
 * keeps the fill's rounded cap. On the DOM backend each part is a rounded `overflow: hidden`
 * window over a border-image strip (the browser loads the art itself); on canvas it is a clip.
 */
export class KeyHuntKeybarFill extends UIElement
{
  private readonly parts: KeybarPart[];
  private fill_end: number;
  private readonly radius: number;
  private dom_parts: { window: HTMLDivElement; strip: HTMLDivElement }[] = [];
  private dom_last_key = "";

  /** `fill_end` is the px from the element's left edge where the fill ends; `radius` is the pills' corner radius. */
  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, parts: KeybarPart[], fill_end: number, radius: number )
  {
    super( ctx, x, y, width, height, false );
    this.parts = parts;
    this.fill_end = fill_end;
    this.radius = radius;
  }

  public name(): string
  {
    return "KeyHuntKeybarFill";
  }

  public setFillEnd( fill_end: number ): void
  {
    this.fill_end = fill_end;
    this.setShouldBeRendered();
  }

  /** Where the strip seen through `part` ends: past the part (cap hidden) once the fill runs beyond it. */
  private drawEnd( part: KeybarPart ): number
  {
    const part_end = part.x + part.w;
    return this.fill_end >= part_end ? part_end + this.rect.getH() : this.fill_end;
  }

  protected override syncDomContent( node: HTMLElement ): boolean
  {
    const h = this.rect.getH();
    const ends = this.parts.map( part => this.fill_end > part.x ? this.drawEnd( part ) : 0 );
    const key = `${this.rect.getW()}|${h}|${ends.join( "," )}`;
    if ( key === this.dom_last_key )
      return true;

    if ( this.dom_parts.length === 0 )
    {
      for ( const part of this.parts )
      {
        const window_el = document.createElement( "div" );
        const ws = window_el.style;
        ws.position = "absolute";
        ws.left = `${part.x}px`;
        ws.top = "0";
        ws.width = `${part.w}px`;
        ws.height = `${h}px`;
        ws.borderRadius = `${this.radius}px`;
        ws.overflow = "hidden";
        ws.pointerEvents = "none";

        const strip = document.createElement( "div" );
        const ss = strip.style;
        ss.position = "absolute";
        ss.left = `${-part.x}px`;
        ss.top = "0";
        ss.height = `${h}px`;

        window_el.appendChild( strip );
        node.appendChild( window_el );
        this.dom_parts.push( { window: window_el, strip } );
      }
    }

    for ( let i = 0; i < this.parts.length; i++ )
    {
      const { window: window_el, strip } = this.dom_parts[i];
      const end = ends[i];
      window_el.style.display = end > 0 ? "" : "none";
      if ( end <= 0 ) continue;

      const spec = nineSliceBorderImage( ImgID.UI_KeyHuntKeybarFill, FILL_RULE, end, h );
      if ( !spec ) return false;
      strip.style.width = `${end}px`;
      applyNineSliceBorderImage( strip.style, spec );
    }

    this.dom_last_key = key;
    return true;
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.applyTransform();

    if ( !this.isDomNative() && this.fill_end > 0 )
    {
      const left = -this.rect.getW2();
      const top = -this.rect.getH2();
      const h = this.rect.getH();
      const image = ImagesHolder.getImage( ImgID.UI_KeyHuntKeybarFill );

      for ( const part of this.parts )
      {
        if ( this.fill_end <= part.x ) break;

        this.ctx.save();
        this.ctx.beginPath();
        this.ctx.roundRect( left + part.x, top, part.w, h, this.radius );
        this.ctx.clip();
        NineSliceDrawer.drawNineSliceImage( this.ctx, image, FILL_RULE, left, top, this.drawEnd( part ), h );
        this.ctx.restore();
      }
    }

    this.finishRender();
  }
}
